// Wire-format types for the Patient Inquiry Management feature (PRD 12).
// Contract: BE returns raw (unmasked) inquirer values; the FE owns name masking
// + code→label mapping per PRD §4.3. See docs/api-contracts/patient-inquiry-management.md.

// ── Enums ────────────────────────────────────────────────────────────

// PRD §4.3 — stored English codes are canonical; Korean labels are FE-mapped.
// APP_USAGE/BUG_ERROR/OTHER added with the V62 inquiry-table merge — the patient-app's own type codes
// now reach this admin screen (wording from the patient-app 문의 유형 dropdown).
export type PatientInquiryType =
  | 'SERVICE_INQUIRY'
  | 'ERROR_REPORT'
  | 'APP_USAGE'
  | 'BUG_ERROR'
  | 'OTHER';

// PRD §4.3 / §5 — PENDING (대기, red) → ANSWERED (답변완료, green) on first reply.
export type PatientInquiryStatus = 'PENDING' | 'ANSWERED';

export type PatientInquiryGender = 'M' | 'F';

// PRD §4.1 + clarification ADM.035 (2026-07-14): only Inquiry Date & Time
// (`inquiredAt`) and Status are interactively sortable; the other keys remain
// in this union for the wire/default but are NOT offered as sort controls.
// `inquiredAt` is the default-sort key. See PATIENT_INQUIRY_SORTABLE_KEYS.
export type PatientInquirySortKey =
  | 'id'
  | 'uid'
  | 'inquirerName'
  | 'title'
  | 'type'
  | 'inquiredAt'
  | 'status';

export type SortDirection = 'asc' | 'desc';

// ── Entities ─────────────────────────────────────────────────────────

// A single reply in the inquiry thread (PRD §3.3 / §4.3). Rendered oldest→newest.
export interface PatientInquiryReply {
  id: number;
  // Admin who authored the reply (display name).
  author: string;
  // ISO-8601 datetime. Rendered as YYYY.MM.DD HH:MM.
  createdAt: string;
  content: string;
}

// List-row projection (PRD §4.1 — 7 columns). Inquirer identity stays raw on
// the wire; masking happens in utils/format-patient-inquiry.
export interface PatientInquiryListItem {
  // Inquiry's own auto-assigned id (PRD §4.1). v9 BIGINT → number.
  id: number;
  // Inquirer patient's system UID, format "U-{id}" (no padding). PRD §4.3.
  uid: string;
  // Raw inquirer name; FE masks per PRD §4.3. Null/"(탈퇴한 사용자)" when withdrawn.
  inquirerName: string | null;
  gender: PatientInquiryGender | null;
  // YYYY-MM-DD (wire) → YYYY.MM.DD (display). Null when withdrawn.
  birthDate: string | null;
  // Inquirer phone, MASKED for display (010-****-5678: carrier prefix + real last 4). The full number is
  // fetched on explicit reveal via getInquirerPhone (audited server-side). Null when withdrawn.
  inquirerPhone: string | null;
  title: string;
  type: PatientInquiryType;
  // ISO-8601 datetime. Rendered as YYYY.MM.DD HH:MM.
  inquiredAt: string;
  status: PatientInquiryStatus;
  // PRD §8.3 — patient account deleted; show "(탈퇴한 사용자)", hide gender/DOB.
  isWithdrawn: boolean;
  // PRD §3.1 / §8.2 — the patient (identity) lookup failed for this row, distinct
  // from a genuine withdrawal. The inquiry stays visible; the inquirer area shows
  // the patient-info error instead of identity. Populated by BE-6 (Option A);
  // absent/false today, so behavior is unchanged until the BE emits it.
  patientLookupFailed?: boolean;
}

// Detail projection (PRD §3.3 slide panel) — list row + body + reply thread.
export interface PatientInquiryDetail extends PatientInquiryListItem {
  content: string;
  replies: PatientInquiryReply[];
}

// ── API contract ─────────────────────────────────────────────────────

export interface ListPatientInquiriesParams {
  // Free-text — case-insensitive partial match on name / phone / title (PRD §3.2).
  keyword?: string;
  page: number;
  perPage: number;
  sortBy: PatientInquirySortKey;
  sortDir: SortDirection;
}

export interface ListPatientInquiriesResult {
  items: PatientInquiryListItem[];
  total: number;
  page: number;
  perPage: number;
}

// PRD §3.3 / §4.2 — reply body. The BE appends a thread entry, sets the author
// from the authenticated admin, and flips PENDING→ANSWERED on the first reply.
export interface CreatePatientInquiryReplyInput {
  // 1..2000 chars, non-blank (PRD §4.2). The FE hard-trims at 2000.
  replyContent: string;
}

// ── Errors ───────────────────────────────────────────────────────────

// Canonical error catalog mirrors docs/api-contracts/patient-inquiry-management.md.
// FE (mock) thrown codes match BE-emitted codes so inline + toast handling is
// identical against either backend.
export type PatientInquiryApiErrorCode =
  // 400 — reply validation
  | 'REPLY_REQUIRED'
  | 'REPLY_TOO_LONG'
  // 401 / 403 / 404
  | 'UNAUTHORIZED'
  | 'INSUFFICIENT_PERMISSION'
  | 'NOT_FOUND'
  // 500 / fallback
  | 'INTERNAL_ERROR'
  | 'UNKNOWN';

export interface PatientInquiryApiErrorMeta {
  [key: string]: unknown;
}

export class PatientInquiryApiError extends Error {
  readonly code: PatientInquiryApiErrorCode;
  readonly fields?: Record<string, string>;
  readonly meta?: PatientInquiryApiErrorMeta;

  constructor(
    code: PatientInquiryApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string>; meta?: PatientInquiryApiErrorMeta },
  ) {
    super(message);
    this.name = 'PatientInquiryApiError';
    this.code = code;
    this.fields = options?.fields;
    this.meta = options?.meta;
    Object.setPrototypeOf(this, PatientInquiryApiError.prototype);
  }
}

// Result of the on-demand phone reveal (PRD §3.3 — the eye button on the inquirer phone). The list/detail carry
// inquirerPhone MASKED; this returns the FULL number for ONE inquiry. Null when withdrawn / no stored phone.
export interface RevealInquirerPhoneResult {
  phone: string | null;
}

export interface PatientInquiryApi {
  listPatientInquiries(
    params: ListPatientInquiriesParams,
  ): Promise<ListPatientInquiriesResult>;
  getPatientInquiry(id: number): Promise<PatientInquiryDetail>;
  // Reveal (decrypt) the inquirer's FULL phone for ONE inquiry on demand (id-scoped; audited server-side).
  getInquirerPhone(id: number): Promise<RevealInquirerPhoneResult>;
  // Saves the reply, appends it to the thread, flips PENDING→ANSWERED on the
  // first reply, and returns the updated detail (PRD §3.3).
  replyPatientInquiry(
    id: number,
    input: CreatePatientInquiryReplyInput,
  ): Promise<PatientInquiryDetail>;
}
